import { ITSelectProps } from '@/components/selects';
import { useWorkspace } from '@/features/workspaces/hooks';
import { Trait } from '@/models/traits';
import { useGetCustomPropertiesQuery, useGetTraitDistributionsQuery } from '@/services/dataSources';
import { useGetTraitsQuery } from '@/services/endpoints/traits';
import _ from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchDropdown } from './SearchDropdown';

type TraitSelectProps = Partial<ITSelectProps<Trait>> & {
  traitId?: string;
  datasourceIds?: string[];
  className?: string;
};

export type DistributionCountMapType = { [key: string]: { count: number; total: number; percent: number } };

export const TraitSelect: FC<TraitSelectProps> = ({ traitId, datasourceIds, className, ...rest }) => {
  const { t } = useTranslation();
  const workspace = useWorkspace();

  const { isLoading, data } = useGetTraitsQuery({
    workspaceId: workspace.id,
  });

  const { data: customPropertiesData } = useGetCustomPropertiesQuery({
    workspaceId: workspace.id,
    datasourceIds: datasourceIds || [],
  });

  const { data: traitDistributionsData } = useGetTraitDistributionsQuery({
    workspaceId: workspace.id,
    datasourceIds: datasourceIds || [],
  });

  const traits = useMemo(() => {
    const customerTraits = data?.customer || [];
    const eventTraits = data?.event || [];

    return _.sortBy([...customerTraits, ...eventTraits], (e) => e.displayName);
  }, [data?.customer, data?.event]);

  const trait = useMemo<any>(() => traits.find((a) => a.id === traitId) || { displayName: traitId }, [traits, traitId]);

  const [searchQuery, setSearchQuery] = useState('');

  const [mergedItems, setMergedItems] = useState<Trait[]>([]);
  const [distributionCountMap, setDistributionCountMap] = useState<DistributionCountMapType>({});

  useEffect(() => {
    if (traitDistributionsData) {
      const map: DistributionCountMapType = {};
      for (const traitDistributiondata of traitDistributionsData) {
        for (const distribution of traitDistributiondata.distributions) {
          const distributionCount = map[distribution.trait.id] || { count: 0, percent: 0, total: 0 };
          distributionCount.total += distribution.total;
          distributionCount.count += distribution.count;
          distributionCount.percent = Math.round((distributionCount.count / distributionCount.total) * 100);
          map[distribution.trait.id] = distributionCount;
        }
      }
      setDistributionCountMap(map);
    }
  }, [traitDistributionsData]);

  useEffect(() => {
    let newMergedItems = [];
    if (traits.length > 0) {
      newMergedItems.push(...traits);
    }
    if (customPropertiesData) {
      newMergedItems.push(
        ...customPropertiesData.properties.map((customProperty) => ({
          id: customProperty.property,
          displayName: `${customProperty.displayName || customProperty.property} (${customProperty.type})`,
          dataType: customProperty.type,
          custom: true,
        }))
      );
    }
    if (searchQuery?.trim()) {
      newMergedItems = newMergedItems.filter((m) => ((m as any).display || m.displayName).toLowerCase().includes(searchQuery?.trim().toLowerCase()));
    }
    setMergedItems(newMergedItems as any);
  }, [traits, customPropertiesData, searchQuery]);

  const labelColor = (percent: number) => {
    if (percent < 35) {
      return 'bg-red-200';
    } else if (percent < 70) {
      return 'bg-yellow-200';
    } else {
      return 'bg-green-200';
    }
  };

  return (
    <SearchDropdown
      items={mergedItems}
      item={trait}
      setItem={(traitItemValue) => {
        console.log('new trait', traitItemValue);
        rest.onChange?.({
          property: String(traitItemValue.id || traitItemValue.displayName),
          dataType: traitItemValue.dataType,
          value: '', // reset value every time a trait is selected
          trait: !!traitItemValue.id && !(traitItemValue as any).custom, //
        });
      }}
      setQuery={setSearchQuery}
      loading={isLoading}
      className={className}
      placeholder={t('audience_edit:property_placeholder') as string}
      openWithFocus={true}
      acceptCustomValue={true}
      readOnly={rest.isViewMode}
    >
      {(item) => (
        <div className={'flex whitespace-nowrap'}>
          {(item as any).display || item.displayName}
          {distributionCountMap[item.id] && (
            <span className={'ml-auto'}>
              <i className={`ml-4 ${labelColor(distributionCountMap[item.id].percent)} `}>%{distributionCountMap[item.id].percent}</i>
            </span>
          )}
        </div>
      )}
    </SearchDropdown>
  );
  // return <TSelect {...rest} items={traits} keyField='id' labelField='displayName' className='w-full text-left' value={trait} />;
};
